import http from './http';
import { cleanParams } from './utils';

/**
 * EMR (Electronic Medical Records) API
 * Provides comprehensive patient medical record management
 */

const ENDPOINTS = {
  PATIENT_EMR: (patientId) => `/api/v1/emr/patient/${patientId}`,
  PATIENT_SUMMARY: (patientId) => `/api/v1/emr/patient/${patientId}/summary`,
  PATIENT_TIMELINE: (patientId) => `/api/v1/emr/patient/${patientId}/timeline`,
  CONSOLIDATE: (patientId) => `/api/v1/emr/patient/${patientId}/consolidate`,
  RECORDS: '/api/v1/emr/records',
  RECORD_BY_ID: (recordId) => `/api/v1/emr/records/${recordId}`
};

const emrAPI = {
  /**
   * Get comprehensive patient EMR
   * This endpoint returns everything: patient info, summary, records, and pagination
   * @param {string} patientId - Patient ID
   * @param {Object} params - Query parameters
   * @param {boolean} params.includeDeleted - Include deleted records
   * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
   * @param {string} params.dateTo - End date (YYYY-MM-DD)
   * @param {string} params.recordTypes - Comma-separated record types
   * @param {number} params.limit - Records per page
   * @param {number} params.page - Page number
   * @returns {Promise}
   */
  getPatientEMR: async (patientId, params = {}) => {
    try {
      const response = await http.get(ENDPOINTS.PATIENT_EMR(patientId), { params: cleanParams(params) });
      return response.data;
    } catch (error) {
      console.error('Get patient EMR error:', error);
      throw error;
    }
  },

  /**
   * Get patient medical summary
   * @param {string} patientId - Patient ID
   * @returns {Promise}
   */
  getPatientSummary: async (patientId) => {
    try {
      const response = await http.get(ENDPOINTS.PATIENT_SUMMARY(patientId));
      return response.data;
    } catch (error) {
      console.error('Get patient summary error:', error);
      throw error;
    }
  },

  /**
   * Get patient medical timeline
   * @param {string} patientId - Patient ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of timeline entries
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @returns {Promise}
   */
  getPatientTimeline: async (patientId, params = {}) => {
    try {
      const response = await http.get(ENDPOINTS.PATIENT_TIMELINE(patientId), { params: cleanParams(params) });
      return response.data;
    } catch (error) {
      console.error('Get patient timeline error:', error);
      throw error;
    }
  },

  /**
   * Consolidate existing patient data into EMR
   * @param {string} patientId - Patient ID
   * @returns {Promise}
   */
  consolidatePatientData: async (patientId) => {
    try {
      const response = await http.post(ENDPOINTS.CONSOLIDATE(patientId));
      return response.data;
    } catch (error) {
      console.error('Consolidate patient data error:', error);
      throw error;
    }
  },

  /**
   * Create new EMR record
   * @param {Object} recordData - EMR record data
   * @returns {Promise}
   */
  createRecord: async (recordData) => {
    try {
      const response = await http.post(ENDPOINTS.RECORDS, recordData);
      return response.data;
    } catch (error) {
      console.error('Create EMR record error:', error);
      throw error;
    }
  },

  /**
   * Get EMR record by ID
   * @param {string} recordId - Record ID
   * @returns {Promise}
   */
  getRecord: async (recordId) => {
    try {
      const response = await http.get(ENDPOINTS.RECORD_BY_ID(recordId));
      return response.data;
    } catch (error) {
      console.error('Get EMR record error:', error);
      throw error;
    }
  },

  /**
   * Update EMR record
   * @param {string} recordId - Record ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise}
   */
  updateRecord: async (recordId, updates) => {
    try {
      const response = await http.put(ENDPOINTS.RECORD_BY_ID(recordId), updates);
      return response.data;
    } catch (error) {
      console.error('Update EMR record error:', error);
      throw error;
    }
  },

  /**
   * Delete EMR record
   * @param {string} recordId - Record ID
   * @returns {Promise}
   */
  deleteRecord: async (recordId) => {
    try {
      const response = await http.delete(ENDPOINTS.RECORD_BY_ID(recordId));
      return response.data;
    } catch (error) {
      console.error('Delete EMR record error:', error);
      throw error;
    }
  }
};

export default emrAPI;
